import { DEFAULT_PRO_FILTER_SET, Service, TimeFilter, useDataService, useProDataService } from 'services/data.service';
import { useQuery } from '@tanstack/react-query';
import { DataRequest, useDataRequest } from 'hooks/useDataRequest';
import { DataResult, Totals } from 'models/data';
import { useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { subDays } from 'date-fns';
import { formatDate } from 'utils/formatDate';
import { FilterValue } from 'components/breadcrumb/breadcrumbModel';
import { ExtendedAttributeCode } from 'pages/trading/model';

export interface DemandKpiValues {
  increased: number;
  decreased: number;
  increaseGrowth: number;
  decreaseGrowth: number;
}

export interface DemandHomeKpiValues {
  categories: DemandKpiValues;
  products: DemandKpiValues;
}

export const useMerchantDemandReportData = () => {
  const { fetchData } = useProDataService(Service.Demand);
  const requestData = useDataRequest();

  const { accountNumber, currentDataVersion } = useContext(TenantConfigContext);

  const startDate = subDays(currentDataVersion ?? new Date(), 7);
  const endDate = subDays(currentDataVersion ?? new Date(), 1);

  return useQuery({
    queryKey: ['merchant-demand-report', accountNumber, requestData],
    queryFn: async (): Promise<DataResult | null> => {
      const fixedTimeRequestData = {
        ...requestData,
        comparisonTimeFilter: null,
        timeFilter: {
          ...requestData.timeFilter,
          fromDate: formatDate(startDate)!,
          toDate: formatDate(endDate)!,
        } as TimeFilter,
      } as DataRequest;

      const response = await fetchData('merchant-demand-report', fixedTimeRequestData);
      if (response.error) {
        throw Error(response.error.message);
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

const mergeDrilldownFilters = (
  dataRequest: DataRequest,
  filters: Record<ExtendedAttributeCode, FilterValue[]> | undefined
) => {
  const orgOrFilter = (org: string[] | undefined, filter: string[] | undefined): string[] | undefined => {
    return filter ?? org;
  };

  return {
    ...dataRequest,
    filters: {
      ...dataRequest.filters,
      categories: orgOrFilter(dataRequest.filters.categories, filters?.prdCategory as string[]),
      productNumbers: orgOrFilter(dataRequest.filters.productNumbers, filters?.prdNumber as string[]),
      brands: orgOrFilter(dataRequest.filters.brands, filters?.prdBrand as string[]),
      marketplaces: orgOrFilter(dataRequest.filters.marketplaces, filters?.chnMarketplace as string[]),
      countries: orgOrFilter(dataRequest.filters.countries, filters?.chnCountry as string[]),
      colors: orgOrFilter(dataRequest.filters.colors, filters?.styBrandColor as string[]),
    },
  };
};

export const useEntriesDemandReportData = (
  endpoint: string,
  filters: Record<ExtendedAttributeCode, FilterValue[]> | undefined = undefined
) => {
  const { fetchData } = useProDataService(Service.Demand);
  const orgRequestData = useDataRequest();
  const requestData = mergeDrilldownFilters(orgRequestData, filters);

  const { accountNumber, currentDataVersion } = useContext(TenantConfigContext);

  const startDate = subDays(currentDataVersion ?? new Date(), 7);
  const endDate = subDays(currentDataVersion ?? new Date(), 1);

  return useQuery({
    queryKey: [endpoint, accountNumber, requestData, filters],
    queryFn: async (): Promise<DataResult | null> => {
      const fixedTimeRequestData = {
        ...requestData,
        comparisonTimeFilter: null,
        timeFilter: {
          ...requestData.timeFilter,
          fromDate: formatDate(startDate)!,
          toDate: formatDate(endDate)!,
        } as TimeFilter,
      } as DataRequest;

      const response = await fetchData('attribute-demand-report/' + endpoint, fixedTimeRequestData);
      if (response.error) {
        throw Error(response.error.message);
      }
      return response.data;
    },
    staleTime: 0,
  });
};

export const useDrillDownKpiData = (filters: Record<ExtendedAttributeCode, FilterValue[]> | undefined) => {
  const { fetchData } = useProDataService(Service.Demand);
  const orgRequestData = useDataRequest();
  const requestData = mergeDrilldownFilters(orgRequestData, filters);

  const { accountNumber, currentDataVersion } = useContext(TenantConfigContext);

  const startDate = subDays(currentDataVersion ?? new Date(), 7);
  const endDate = subDays(currentDataVersion ?? new Date(), 1);
  const compStartDate = subDays(startDate ?? new Date(), 7);
  const compEndDate = subDays(endDate ?? new Date(), 7);

  return useQuery({
    queryKey: ['drilldown-kpis', accountNumber, requestData, filters],
    queryFn: async (): Promise<Totals | null> => {
      const fixedTimeRequestData = {
        ...requestData,
        comparisonTimeFilter: {
          ...requestData.comparisonTimeFilter,
          fromDate: formatDate(compStartDate),
          toDate: formatDate(compEndDate),
        },
        timeFilter: {
          ...requestData.timeFilter,
          fromDate: formatDate(startDate)!,
          toDate: formatDate(endDate)!,
        } as TimeFilter,
      } as DataRequest;

      const response = await fetchData('drilldown-kpis', fixedTimeRequestData);
      if (response.error) {
        throw Error(response.error.message);
      }
      return response.data;
    },
    staleTime: 0,
  });
};

export const useDemandHomeKpiData = () => {
  const { fetchData } = useProDataService(Service.Demand);
  const requestData = useDataRequest();

  const { accountNumber, currentDataVersion } = useContext(TenantConfigContext);

  const startDate = subDays(currentDataVersion ?? new Date(), 7);
  const endDate = subDays(currentDataVersion ?? new Date(), 1);
  const compStartDate = subDays(startDate ?? new Date(), 7);
  const compEndDate = subDays(endDate ?? new Date(), 7);

  return useQuery({
    queryKey: ['drilldown-kpis', accountNumber, requestData],
    queryFn: async (): Promise<DemandHomeKpiValues | null> => {
      const fixedTimeRequestData = {
        ...requestData,
        comparisonTimeFilter: {
          ...requestData.comparisonTimeFilter,
          fromDate: formatDate(compStartDate),
          toDate: formatDate(compEndDate),
        },
        timeFilter: {
          ...requestData.timeFilter,
          fromDate: formatDate(startDate)!,
          toDate: formatDate(endDate)!,
        } as TimeFilter,
      } as DataRequest;

      const response = await fetchData('demand-home-kpis', fixedTimeRequestData);
      if (response.error) {
        throw Error(response.error.message);
      }
      return response.data as unknown as DemandHomeKpiValues;
    },
    staleTime: 0,
  });
};

export const useProductCategory = (filters: Record<ExtendedAttributeCode, FilterValue[]>) => {
  const getData = useDataService(Service.Products, DEFAULT_PRO_FILTER_SET).get<string>(`product-category`);
  const requestData = useDataRequest();
  const { accountNumber } = useContext(TenantConfigContext);

  return useQuery({
    queryKey: ['productCategory', accountNumber, filters],
    queryFn: async (): Promise<string> => {
      if (filters.prdCategory) return filters.prdCategory[0] as string;
      const productNumber = filters.prdNumber[0] as string;
      if (!productNumber) return '';

      const amendedRequestData = {
        ...requestData,
        filters: {
          productNumbers: [productNumber],
        },
      };

      const response = await getData('', amendedRequestData);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }

      return response.data!;
    },
    staleTime: 0,
  });
};
